import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getMyReports } from "api/community";
import { useSnackbar } from "contexts/SnackbarContext";
import SubHeader from "components/common/SubHeader";
import NoData from "components/common/NoData";
import dayjs from "dayjs";
import "pages/community/community.scss";

const REASON_LABELS = {
  spam: "스팸/광고",
  harassment: "괴롭힘/혐오",
  inappropriate: "부적절한 콘텐츠",
  violence: "폭력/위협",
  false_info: "허위 정보",
  other: "기타",
};

const STATUS_LABELS = {
  pending: "접수",
  reviewing: "검토중",
  resolved: "처리완료",
  dismissed: "반려",
};

const STATUS_COLORS = {
  pending: "#f59e0b",
  reviewing: "#3b82f6",
  resolved: "#10b981",
  dismissed: "#6b7280",
};

const TARGET_TYPE_LABELS = {
  post: "게시글",
  comment: "댓글",
  user: "사용자",
};

const getTargetLink = (report) => {
  if (report.target_type === "post") return `/community/${report.target_id}`;
  if (report.target_type === "comment") return `/community/${report.target_id}`;
  // 신고 레코드의 target_id 는 내부 식별자라 프로필 URL 에 쓸 수 없다.
  // 공개 식별자가 없으면 링크를 만들지 않는다.
  if (report.target_type === "user") {
    return report.target_public_id
      ? `/community/user/${encodeURIComponent(report.target_public_id)}`
      : null;
  }
  return null;
};

const MyReports = () => {
  const navigate = useNavigate();
  const { showError } = useSnackbar();
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const loadReports = useCallback(
    async (pageNum = 1, append = false) => {
      try {
        setIsLoading(true);
        const result = await getMyReports(pageNum, 20);
        if (result.success) {
          const items = result.data || [];
          setReports((prev) => (append ? [...prev, ...items] : items));
          setHasMore(result.meta?.hasMore ?? false);
          setPage(pageNum);
        }
      } catch {
        showError("신고 목록을 불러오지 못했습니다.");
      } finally {
        setIsLoading(false);
      }
    },
    [showError]
  );

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  return (
    <div className="community-page my-reports-page">
      <SubHeader title="내 신고 내역" />

      <div className="my-reports-content">
        {isLoading && reports.length === 0 ? (
          <div className="my-reports-loading">불러오는 중...</div>
        ) : reports.length === 0 ? (
          <NoData title="신고 내역이 없습니다." />
        ) : (
          <>
            <div className="my-reports-list">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="my-report-item"
                  onClick={() => {
                    const link = getTargetLink(report);
                    if (link) navigate(link);
                  }}
                  style={{ cursor: getTargetLink(report) ? "pointer" : "default" }}
                >
                  <div className="my-report-header">
                    <span className="my-report-target-type">
                      {TARGET_TYPE_LABELS[report.target_type] || report.target_type}
                    </span>
                    <span
                      className="my-report-status"
                      style={{
                        color: STATUS_COLORS[report.status] || "#6b7280",
                      }}
                    >
                      {STATUS_LABELS[report.status] || report.status}
                    </span>
                  </div>
                  <div className="my-report-reason">
                    {REASON_LABELS[report.reason] || report.reason}
                  </div>
                  {report.description && (
                    <div className="my-report-description">
                      {report.description}
                    </div>
                  )}
                  <div className="my-report-date">
                    {dayjs(report.created_at).format("YYYY.MM.DD HH:mm")}
                  </div>
                </div>
              ))}
            </div>
            {hasMore && (
              <button
                className="my-reports-load-more"
                onClick={() => loadReports(page + 1, true)}
                disabled={isLoading}
              >
                {isLoading ? "불러오는 중..." : "더보기"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyReports;
